import React, {useEffect, useMemo, useReducer} from 'react';
import {Helmet} from 'react-helmet';
import {CSSTransition} from 'react-transition-group';
import {compose} from 'redux';
import ctaBackground from '../../assets/images/cta_back_signup.jpg';
import './styles.css';
import ApplyPositionForm from '../../components/ApplyPositionForm';
import BasicButton from '../../components/BasicButton';
import FiltersContentLoader from '../../components/FiltersContentLoader';
import JobContentLoader from '../../components/JobContentLoader';
import JobDetails from '../../components/JobDetails';
import PanelContainer from '../../components/PanelContainer';
import ProjectDetails from '../../components/ProjectDetails';
import ShareButton from '../../components/ShareButton';
import {ANIMATION_TIMEOUT_APPEAR} from '../../constants/animation';
import ENDPOINT from '../../constants/endpoints';
import ROUTE from '../../constants/route';
import LinkBuilder from '../../helpers/link-builder';
import {linkedInShareUrl} from '../../helpers/share';
import StringHelpers from '../../helpers/string-helpers';
import {Role, showNoCandidateProfileDialog} from '../../helpers/user';
import withProcessApplyAction from '../../hocs/withProcessApplyAction';
import useApi from '../../hooks/useApi';
import PageLayout from '../../layouts/PageLayout';
import Sidebar from '../../scenes/Sidebar';
import CtaBanner from '../../scenes/Sidebar/components/CtaBanner';
import InBounds from '../../scenes/Sidebar/components/InBounds';
import RelatedJobs from '../../scenes/Sidebar/components/RelatedJobs';
import {action} from './state/actions';
import {initialState} from './state/initialState';
import {reducer} from './state/reducer';

const PROCESS_TYPE = {
  PROCESS: 'process',
  PROJECT: 'project',
};

const PublicProcessProfile = props => {
  const {
    user,
    history,
    globalActions: { updateUserInboundsCount },
    applyActions,
    match: {
      params: { id, type },
    },
  } = props;

  const api = useApi();

  const [state, dispatch] = useReducer(reducer, initialState);

  const [isAnonymous, isCandidate] = useMemo(
    () => [Role.isAnonymous(user), Role.isCandidate(user)],
    [user]
  );

  const processStatus = useMemo(
    () =>
      state.process
        ? state.process.IsClosed === 'Yes'
          ? 'closed'
          : state.process.isApplied
          ? 'applied'
          : null
        : null,
    [state.process]
  );

  useEffect(() => {
    dispatch(action.processRequestData());
    api.get(
      PROCESS_TYPE.PROCESS === type
        ? ENDPOINT.ENTITY.PUBLIC_JOB
        : ENDPOINT.ENTITY.PUBLIC_PROJECT,
      { id },
      {
        handleError: () => history.replace(ROUTE.ERROR_404),
        handleSuccess: process => dispatch(action.processSetData(process)),
      }
    );
  }, [history.location]);

  const openApplyForm = () => {
    dispatch(action.setFormVisible(true));
  };

  const hideApplyForm = () => {
    dispatch(action.setApplyButtonEnabled(true));
    dispatch(action.setFormVisible(false));
  };

  const handleApplyJob = () => {
    if (isAnonymous) {
      window.location = window.location.origin + '/signup';
    } else if (isCandidate) {
      dispatch(action.setApplyButtonEnabled(false));
      applyActions
        .check(state.process)
        .then(success =>
          success
            ? openApplyForm()
            : dispatch(action.setApplyButtonEnabled(true))
        );
    } else {
      showNoCandidateProfileDialog(user);
    }
  };

  const handleApply = () => {
    applyActions
      .apply(state.process)
      .then(success =>
        success ? dispatch(action.processSetApplied(true)) : hideApplyForm()
      );
  };

  const openLinkedInSharingWindow = () =>
    linkedInShareUrl(
      window.location.origin + LinkBuilder.getProcessPublicLink(id, type)
    );

  return (
    <PageLayout
      pageClass="job-page"
      mainContent={
        state.init && state.process ? (
          <CSSTransition
            in={true}
            appear={true}
            timeout={ANIMATION_TIMEOUT_APPEAR}
            classNames="fade"
          >
            <PanelContainer>
              <Helmet>
                <title>{state.process.PositionTitle}</title>
                <meta name="robots" content="noindex, nofollow"/>
              </Helmet>
              {PROCESS_TYPE.PROCESS === type ? (
                <JobDetails
                  job={state.process}
                  showDeadlineSendInBound={isCandidate}
                  showBaseStartDate={false}
                />
              ) : (
                <ProjectDetails
                  project={state.process}
                  showDeadlineSendInBound={isCandidate}
                  showBaseStartDate={false}
                />
              )}
              {state.isFormOpen && !state.process.Status ? (
                <ApplyPositionForm
                  onApplyHandler={handleApply}
                  onRefuseHandler={hideApplyForm}
                  message={`Do you want to apply for this ${
                    PROCESS_TYPE.PROCESS === type ? 'position' : 'project'
                  }?`}
                />
              ) : (
                <div className="process-controls">
                  {processStatus ? (
                    <div className="process-controls__disabled-btn">
                      {StringHelpers.ucfirst(processStatus)}
                    </div>
                  ) : (
                    <div className="process-controls__apply-btn">
                      <BasicButton
                        disabled={state.isApplyButtonDisabled}
                        btnWidth={200}
                        btnHeight={40}
                        btnFontWeight={600}
                        btnText="Apply"
                        btnArrow={true}
                        btnBg="#fff"
                        btnHoverBg="#f40"
                        controlFunc={handleApplyJob}
                      />
                    </div>
                  )}

                  {state.process.IsClosed === 'No' &&
                    state.process.BlockLinkedInSharing === 'No' &&
                    state.process.Approved === 'Yes' && (
                      <ShareButton
                        caption="Share on Linkedin"
                        separatorLeft={true}
                        action={openLinkedInSharingWindow}
                      />
                    )}
                </div>
              )}
            </PanelContainer>
          </CSSTransition>
        ) : (
          <div className="horizontal-loader__container">
            <JobContentLoader />
          </div>
        )
      }
      sidebarContent={
        <CSSTransition
          in={true}
          appear={true}
          timeout={ANIMATION_TIMEOUT_APPEAR}
          classNames="fade"
        >
          {state.init ? (
            <Sidebar>
              <>
                {isAnonymous && (
                  <CtaBanner
                    blockHeight="150"
                    blockBg={ctaBackground}
                    blockText="Join The GoBuyside Platform"
                    btnText="Sign Up"
                    btnLink="/signup"
                    blockFontSize="20"
                  />
                )}
                {!isCandidate && <RelatedJobs user={user} history={history} />}
                {isCandidate && (
                  <InBounds
                    inboundsCount={user.inbounds}
                    onInboundsUpdate={updateUserInboundsCount}
                  />
                )}
              </>
            </Sidebar>
          ) : (
            <FiltersContentLoader />
          )}
        </CSSTransition>
      }
    />
  );
};

export default compose(withProcessApplyAction)(PublicProcessProfile);
